<script setup lang="ts">
import { ref } from 'vue'
import DecorativeShapeItem from './DecorativeShapeItem.vue'
import Grid from './Grid.vue'
import LabelItem from './LabelItem.vue'
import Walls from './Walls.vue'
import { DEFAULT_GRID_SIZE } from '~/lib/seatmapeditor/constants'
import type { DecorativeShape, Label, Point, Seat, VisibleRowEntry, Wall } from '~/lib/seatmapeditor/types'

interface Props {
	visibleRows: VisibleRowEntry[]
	walls: Wall[]
	selectedWallIds: string[]
	pendingWallStart: Point | null
	pointerDownPoint: Point | null
	pointerMovePoint: Point | null
	pointerUpPoint: Point | null
	labels: Label[]
	selectedLabelId: number | null
	decorativeShapes: DecorativeShape[]
	selectedShapeId: string | null
	selectedRowActionStyle: Record<string, string> | null
	selectedSeatActionStyle: Record<string, string> | null
	editMode: 'seats' | 'walls'
	seatEditMode: 'rows' | 'seats'
	snapToGrid: boolean
	showGrid: boolean
	background?: string
	gridSize?: number
	gridSizeX?: number
	gridSizeY?: number
	gridOffsetX?: number
	gridOffsetY?: number
	isSeatSelected: (rowIndex: number, seatIndex: number) => boolean
	isRowSelected: (rowIndex: number) => boolean
	isRowDragging: (rowIndex: number) => boolean
	getSeatConfig: (rowIndex: number, seatIndex: number) => Record<string, unknown>
	getSeatRectConfig: (rowIndex: number, seatIndex: number, seat: Seat) => Record<string, unknown>
	getSeatTextConfig: (rowIndex: number, seatIndex: number, seat: Seat) => Record<string, unknown>
	shouldShowSeatText: (rowIndex: number, seatIndex: number) => boolean
	shouldShowSelectedSeatDeleteButton: (rowIndex: number, seatIndex: number) => boolean
	getSelectedSeatDeleteButtonConfig: () => Record<string, unknown>
	getDeleteSeatButtonRectConfig: () => Record<string, unknown>
	getDeleteSeatButtonBinBodyConfig: () => Record<string, unknown>
	getDeleteSeatButtonBinLidConfig: () => Record<string, unknown>
	getDeleteSeatButtonBinHandleConfig: () => Record<string, unknown>
	shouldShowAddSeatButton: (rowIndex: number) => boolean
	getAddSeatButtonConfig: (rowIndex: number) => Record<string, unknown>
	getAddSeatButtonRectConfig: () => Record<string, unknown>
	getAddSeatButtonTextConfig: () => Record<string, unknown>
	shouldShowDeleteSeatButton: (rowIndex: number) => boolean
	getDeleteSeatButtonConfig: (rowIndex: number) => Record<string, unknown>
	getRowLabelConfig: (rowIndex: number) => Record<string, unknown>
}

const props = withDefaults(defineProps<Props>(), {
	background: '#f8fafc',
	gridSize: DEFAULT_GRID_SIZE,
	gridOffsetX: 0,
	gridOffsetY: 0
})

const emit = defineEmits<{
	(e: 'canvas-click', payload: Point): void
	(e: 'canvas-pointerdown', payload: Point): void
	(e: 'canvas-pointermove', payload: Point): void
	(e: 'canvas-pointerup', payload: Point): void
	(e: 'camera-change', payload: { x: number; y: number; scale: number; viewportWidth: number; viewportHeight: number }): void
	(e: 'walls-change', payload: Wall[]): void
	(e: 'selection-change', payload: string[]): void
	(e: 'pending-start-change', payload: Point | null): void
	(e: 'open-row-options'): void
	(e: 'open-seat-options'): void
	(e: 'seat-click', rowIndex: number, seatIndex: number, event: Event): void
	(e: 'seat-dragstart', rowIndex: number, seatIndex: number, node: any): void
	(e: 'seat-dragmove', rowIndex: number, seatIndex: number, node: any): void
	(e: 'seat-dragend', rowIndex: number, seatIndex: number, node: any): void
	(e: 'selected-seat-delete-pointerdown', event: Event): void
	(e: 'selected-seat-delete-click', event: Event): void
	(e: 'add-seat-button-pointerdown', event: Event): void
	(e: 'add-seat-button-click', rowIndex: number, event: Event): void
	(e: 'delete-seat-button-pointerdown', event: Event): void
	(e: 'delete-seat-button-click', rowIndex: number, event: Event): void
	(e: 'row-label-click', rowIndex: number, event: Event): void
	(e: 'row-label-dragstart', rowIndex: number, node: any): void
	(e: 'row-label-dragmove', rowIndex: number, node: any): void
	(e: 'row-label-dragend', rowIndex: number, node: any): void
	(e: 'label-select', labelId: number): void
	(e: 'label-position-changed', labelId: number, x: number, y: number): void
	(e: 'shape-select', shapeId: string): void
	(e: 'shape-position-changed', shapeId: string, x: number, y: number): void
	(e: 'shape-resize-changed', shapeId: string, w: number, h: number): void
}>()

const gridRef = ref<{
	resetZoom?: () => void
	focusWorldBounds?: (bounds: { minX: number; minY: number; maxX: number; maxY: number }, padding?: number) => void
} | null>(null)

const openRowOptions = () => {
	emit('open-row-options')
}

const openSeatOptions = () => {
	emit('open-seat-options')
}

defineExpose({
	resetZoom: () => gridRef.value?.resetZoom?.(),
	focusWorldBounds: (bounds: { minX: number; minY: number; maxX: number; maxY: number }, padding?: number) => gridRef.value?.focusWorldBounds?.(bounds, padding)
})
</script>

<template>
	<div class="canvas-wrap">
		<div
			v-if="selectedRowActionStyle"
			class="row-action-bar"
			:style="selectedRowActionStyle"
		>
			<button
				type="button"
				class="row-settings-btn"
				@click="openRowOptions"
			>
				<i class="fa fa-fw fa-cog"></i>
			</button>
		</div>

		<div
			v-if="selectedSeatActionStyle"
			class="row-action-bar"
			:style="selectedSeatActionStyle"
		>
			<button
				type="button"
				class="row-settings-btn"
				@click="openSeatOptions"
			>
				<i class="fa fa-fw fa-cog"></i>
			</button>
		</div>

		<Grid
			ref="gridRef"
			:grid-size="gridSize"
			:grid-size-x="gridSizeX ?? gridSize"
			:grid-size-y="gridSizeY ?? gridSize"
			:grid-offset-x="gridOffsetX"
			:grid-offset-y="gridOffsetY"
			:show-grid-lines="showGrid"
			:pan-enabled="editMode !== 'walls'"
			:background="background"
			@canvas-click="emit('canvas-click', $event)"
			@canvas-pointerdown="emit('canvas-pointerdown', $event)"
			@canvas-pointermove="emit('canvas-pointermove', $event)"
			@canvas-pointerup="emit('canvas-pointerup', $event)"
			@camera-change="emit('camera-change', $event)"
		>
			<Walls
				:walls="walls"
				:selected-wall-ids="selectedWallIds"
				:pending-wall-start="pendingWallStart"
				:pointer-down-point="pointerDownPoint"
				:pointer-move-point="pointerMovePoint"
				:pointer-up-point="pointerUpPoint"
				:edit-mode="editMode"
				:snap-to-grid="snapToGrid"
				:grid-size="gridSize"
				@walls-change="emit('walls-change', $event)"
				@selection-change="emit('selection-change', $event)"
				@pending-start-change="emit('pending-start-change', $event)"
			/>

			<v-group
				v-for="rowEntry in visibleRows"
				:key="`row-${rowEntry.rowIndex}`"
			>
				<v-group
					v-for="seatEntry in rowEntry.seats"
					:key="`seat-${rowEntry.rowIndex}-${seatEntry.seatIndex}`"
					:config="getSeatConfig(rowEntry.rowIndex, seatEntry.seatIndex)"
					@dragstart="emit('seat-dragstart', rowEntry.rowIndex, seatEntry.seatIndex, $event.target)"
					@dragmove="emit('seat-dragmove', rowEntry.rowIndex, seatEntry.seatIndex, $event.target)"
					@dragend="emit('seat-dragend', rowEntry.rowIndex, seatEntry.seatIndex, $event.target)"
				>
					<v-rect
						key="seat-rect"
						:config="getSeatRectConfig(rowEntry.rowIndex, seatEntry.seatIndex, seatEntry.seat)"
						@click="emit('seat-click', rowEntry.rowIndex, seatEntry.seatIndex, $event)"
					/>
					<v-text
						v-if="shouldShowSeatText(rowEntry.rowIndex, seatEntry.seatIndex)"
						key="seat-text"
						:config="getSeatTextConfig(rowEntry.rowIndex, seatEntry.seatIndex, seatEntry.seat)"
					/>
					<v-group
						v-if="shouldShowSelectedSeatDeleteButton(rowEntry.rowIndex, seatEntry.seatIndex)"
						key="seat-delete-button"
						:config="getSelectedSeatDeleteButtonConfig()"
						@mousedown="emit('selected-seat-delete-pointerdown', $event)"
						@touchstart="emit('selected-seat-delete-pointerdown', $event)"
						@click="emit('selected-seat-delete-click', $event)"
					>
						<v-rect key="bin-rect" :config="getDeleteSeatButtonRectConfig()" />
						<v-rect key="bin-body" :config="getDeleteSeatButtonBinBodyConfig()" />
						<v-rect key="bin-lid" :config="getDeleteSeatButtonBinLidConfig()" />
						<v-rect key="bin-handle" :config="getDeleteSeatButtonBinHandleConfig()" />
					</v-group>
				</v-group>
				<v-group
					v-if="shouldShowAddSeatButton(rowEntry.rowIndex)"
					:key="`add-seat-${rowEntry.rowIndex}`"
					:config="getAddSeatButtonConfig(rowEntry.rowIndex)"
					@mousedown="emit('add-seat-button-pointerdown', $event)"
					@touchstart="emit('add-seat-button-pointerdown', $event)"
					@click="emit('add-seat-button-click', rowEntry.rowIndex, $event)"
				>
					<v-rect key="add-seat-rect" :config="getAddSeatButtonRectConfig()" />
					<v-text key="add-seat-text" :config="getAddSeatButtonTextConfig()" />
				</v-group>
				<v-group
					v-if="shouldShowDeleteSeatButton(rowEntry.rowIndex)"
					:key="`delete-seat-${rowEntry.rowIndex}`"
					:config="getDeleteSeatButtonConfig(rowEntry.rowIndex)"
					@mousedown="emit('delete-seat-button-pointerdown', $event)"
					@touchstart="emit('delete-seat-button-pointerdown', $event)"
					@click="emit('delete-seat-button-click', rowEntry.rowIndex, $event)"
				>
					<v-rect key="row-delete-rect" :config="getDeleteSeatButtonRectConfig()" />
					<v-rect key="row-delete-bin-body" :config="getDeleteSeatButtonBinBodyConfig()" />
					<v-rect key="row-delete-bin-lid" :config="getDeleteSeatButtonBinLidConfig()" />
					<v-rect key="row-delete-bin-handle" :config="getDeleteSeatButtonBinHandleConfig()" />
				</v-group>
			</v-group>

			<v-text
				v-for="rowEntry in visibleRows"
				:key="`label-${rowEntry.rowIndex}`"
				:config="getRowLabelConfig(rowEntry.rowIndex)"
				@click="emit('row-label-click', rowEntry.rowIndex, $event)"
				@dragstart="emit('row-label-dragstart', rowEntry.rowIndex, $event.target)"
				@dragmove="emit('row-label-dragmove', rowEntry.rowIndex, $event.target)"
				@dragend="emit('row-label-dragend', rowEntry.rowIndex, $event.target)"
			/>

			<DecorativeShapeItem
				v-if="editMode === 'seats'"
				v-for="shape in decorativeShapes"
				:key="shape.id"
				:shape="shape"
				:is-selected="selectedShapeId === shape.id"
				@select="emit('shape-select', shape.id)"
				@position-changed="(x: number, y: number) => emit('shape-position-changed', shape.id, x, y)"
				@resize-changed="(w: number, h: number) => emit('shape-resize-changed', shape.id, w, h)"
			/>

			<LabelItem
				v-if="editMode === 'seats'"
				v-for="label in labels"
				:key="label.id"
				:label="label"
				:is-selected="selectedLabelId === label.id"
				@select="emit('label-select', label.id)"
				@position-changed="(x: number, y: number) => emit('label-position-changed', label.id, x, y)"
			/>

		</Grid>
	</div>
</template>

<style scoped>
.canvas-wrap {
	flex: 1;
	background: transparent;
	overflow: hidden;
	position: relative;
	height: 100%;
	min-height: 0;
	user-select: none;
	-webkit-user-select: none;
}

.row-action-bar {
	position: absolute;
	transform: translateY(-100%);
	height: 40px;
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 34;
	pointer-events: none;
}

.row-settings-btn {
	width: 40px;
	height: 40px;
	display: grid;
	place-items: center;
	border: 1px solid #94a3b8;
	border-radius: 8px;
	background: #ffffff;
	color: #0f172a;
	box-shadow: 0 8px 18px rgba(15, 23, 42, 0.18);
	cursor: pointer;
	transition: background 0.12s ease;
	pointer-events: auto;
	touch-action: manipulation;
	user-select: none;
	-webkit-user-select: none;
	-webkit-tap-highlight-color: transparent;
	font-size: 1rem;
}

.row-settings-btn:hover {
	background: #e2e8f0;
}

.row-settings-btn:active {
	background: #d1d5db;
}
</style>